/**
 * The vendor's own strings, in a quiet labelled slot.
 *
 * Lane names, tool names, accession numbers, ISRCs, CIKs, revision ids, result
 * ids, geo syntax, MBIDs, pipeline names, MIME types and licence prose all go
 * here and nowhere else. They are not evidence; they are how the evidence was
 * addressed, and putting one in a chip beside a title gives it the weight of
 * a fact about the thing itself.
 *
 * The two slots are typed DIFFERENTLY on purpose: `primary` is what a reader
 * needs (an as-of, a page bound, a licence) and takes one string, while the
 * vendor's internal handles are a list that lives only inside the disclosure.
 * A vendor string cannot be passed as the primary statement, because the
 * shapes do not match.
 *
 * `primary` is also OPTIONAL, and that is the load-bearing half of the split:
 * it is the visible half. With handles present it is the disclosure's summary,
 * and with none it is a bare visible line — so a set whose provenance is
 * nothing but internal handles must pass no primary at all, or it publishes on
 * a visible line exactly the strings the disclosure exists to keep collapsed.
 */
"use client";
import type { ReactElement } from "react";
import {
  CRC_NUMERIC_STYLE,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import { CresultcardTextStep } from "./cresultcard.types";
import { CresultcardDisclosure } from "./CresultcardDisclosure";

const PROVENANCE_LABEL = "Provenance";
const HANDLE_GAP = 3;

interface CresultcardProvenanceProps {
  /**
   * What a reader needs: as-of, page bounds, licence. ABSENT where the slot
   * carries nothing but vendor handles — this half is visible.
   */
  readonly primary?: string;
  /** The vendor's internal handles. Inside the disclosure, always. */
  readonly secondary?: readonly string[];
}

export function CresultcardProvenance({
  primary,
  secondary = [],
}: CresultcardProvenanceProps): ReactElement | null {
  if (secondary.length === 0) {
    if (primary == null) {
      return null;
    }
    return (
      <span
        data-el="Cresultcard/Provenance/Root"
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
          ...CRC_NUMERIC_STYLE,
          fontSize: 9.5,
          color: CRC_TEXT_FAINT,
        }}
      >
        {primary}
      </span>
    );
  }

  return (
    <CresultcardDisclosure
      label={PROVENANCE_LABEL}
      summary={primary ?? null}
    >
      <div
        data-el="Cresultcard/Provenance/Panel"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: HANDLE_GAP,
          minWidth: 0,
        }}
      >
        {secondary.map((handle) => (
          <span
            key={handle}
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
              ...CRC_NUMERIC_STYLE,
              fontSize: 9.5,
              overflowWrap: "anywhere",
              color: CRC_TEXT_FAINT,
            }}
          >
            {handle}
          </span>
        ))}
      </div>
    </CresultcardDisclosure>
  );
}
